"use client";

import { useState, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquare, ArrowLeft, Mail, Phone } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "sonner";
import { AutoReplyFeedbackTable } from "@/components/agents/email/auto-reply-feedback-table";
import { AutoReplyViewerDialog, type AutoReplyDetails } from "@/components/agents/email/dialogs";
import { VoiceCallFeedbackTable } from "@/components/calls/voice-call-feedback-table";
import { CallDetailsDialog } from "@/components/calls/call-details-dialog";
import { useTranslations } from "next-intl"


type AgentType = "email" | "voice";

export function QAFeedbackPage() {
  const tUi = useTranslations("Ai.AiFeedbackQaFeedbackPage")

  const { user, fetchWithAuth } = useAuth();
  const { can } = usePermissions();
  const searchParams = useSearchParams();
  const router = useRouter();

  // Email agent params
  const agentId = searchParams.get("agentId");
  const agentName = searchParams.get("agentName");

  // Voice agent params
  const voiceAgentId = searchParams.get("voiceAgentId");
  const voiceAgentName = searchParams.get("agentName");

  // Determine initial tab based on URL params
  const initialTab: AgentType = voiceAgentId ? "voice" : "email";
  const [activeTab, setActiveTab] = useState<AgentType>(initialTab);

  const [autoReplyViewerOpen, setAutoReplyViewerOpen] = useState(false);
  const [selectedAutoReply, setSelectedAutoReply] = useState<AutoReplyDetails | null>(null);

  // Call details dialog state
  const [callDetailsOpen, setCallDetailsOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  // Update tab when URL params change
  useEffect(() => {
    if (voiceAgentId) {
      setActiveTab("voice");
    } else if (agentId) {
      setActiveTab("email");
    }
  }, [voiceAgentId, agentId]);

  async function openAutoReplyDetails(autoReplyId: string) {
    if (!user?.uuid) {
      toast.error(tUi("youMustBeLoggedInToViewAuto"));
      return;
    }
    try {
      const res = await fetchWithAuth(`/api/agents/email/auto-reply/${autoReplyId}`);
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || "Failed to fetch auto-reply");
      }

      setSelectedAutoReply(json.autoReply);
      setAutoReplyViewerOpen(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load auto-reply details";
      toast.error(errorMessage);
    }
  }

  const handleBack = () => {
    if (voiceAgentId) {
      router.push("?page=voice-agents");
    } else if (agentId) {
      router.push(`?page=agent-emails&agentId=${agentId}`);
    } else {
      router.push("?page=email-agents");
    }
  };

  const openCallDetails = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setCallDetailsOpen(true);
  };

  // Get display name for the current agent
  const displayAgentName = voiceAgentId ? voiceAgentName : agentName;
  const hasSpecificAgent = agentId || voiceAgentId;

  return (
    <div className="space-y-6">
      <Card className="min-w-0 shadow-sm">
        <CardHeader className="px-4 pb-4 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            {hasSpecificAgent && (
              <Button variant="ghost" size="sm" onClick={handleBack} className="w-fit sm:mr-2">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span><span key={tUi("back")}>{tUi("back")}</span></span>
              </Button>
            )}
            <div className="flex min-w-0 items-start gap-3">
              <div className="shrink-0 p-2 bg-orange-100 rounded-lg">
                <MessageSquare className="w-6 h-6 text-orange-600" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-xl font-semibold break-words">
                  <span><span key={tUi("qAFeedback")}>{tUi("qAFeedback")}</span></span>
                  {displayAgentName && <span className="text-muted-foreground font-normal"> - {displayAgentName}</span>}
                </CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  <span><span key={tUi("reviewAndManageFeedbackOnAIGeneratedResponses")}>{tUi("reviewAndManageFeedbackOnAIGeneratedResponses")}</span></span>
                  <span>{hasSpecificAgent && " for this agent"}</span>
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="min-w-0 shadow-sm">
        <div className="p-4 sm:p-6">
          {/* Show tabs only when no specific agent is selected */}
          {!hasSpecificAgent ? (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as AgentType)}>
              <TabsList className="mb-4">
                <TabsTrigger value="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span><span key={tUi("emailAgents")}>{tUi("emailAgents")}</span></span>
                </TabsTrigger>
                <TabsTrigger value="voice" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span><span key={tUi("voiceAgents")}>{tUi("voiceAgents")}</span></span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="email">
                <AutoReplyFeedbackTable
                  onViewDetails={openAutoReplyDetails}
                  agentId={undefined}
                />
              </TabsContent>

              <TabsContent value="voice">
                <VoiceCallFeedbackTable
                  voiceAgentId={undefined}
                  onViewDetails={openCallDetails}
                />
              </TabsContent>
            </Tabs>
          ) : voiceAgentId ? (
            <VoiceCallFeedbackTable
              voiceAgentId={voiceAgentId}
              onViewDetails={openCallDetails}
            />
          ) : (
            <AutoReplyFeedbackTable
              onViewDetails={openAutoReplyDetails}
              agentId={agentId || undefined}
            />
          )}
        </div>
      </Card>

      <AutoReplyViewerDialog
        open={autoReplyViewerOpen}
        onOpenChange={setAutoReplyViewerOpen}
        autoReply={selectedAutoReply}
        userTimezone={user?.timezone as string | undefined}
        showCrmLink={can("crm.view")}
      />

      <CallDetailsDialog
        open={callDetailsOpen}
        onOpenChange={setCallDetailsOpen}
        conversationId={selectedConversationId}
      />
    </div>
  );
}
